import React, { useState } from "react";

import Button from "@common/components/Button/Button";
import MessageInput from "@common/components/Input/MessageInput/MessageInput";
import { ModalHeader } from "@common/components/Modal/ModalHeader/ModalHeader";
import { PlatformName } from "@common/models";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { docLink } from "@common/util/docs";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { RoutePaths } from "@main/router/config/routePaths";
import { Row } from "@tanstack/react-table";
import { generatePath, useNavigate } from "react-router-dom";

import * as Styled from "./ModalBody.styles";

export function NoteMessageModalBody<TData>({
  title,
  confirmAction,
  isNote,
  close,
  selectedRows,
  numOfAppUsers,
}: {
  title: string;
  confirmAction: (rows: TData[], message: string, isNote?: boolean) => void;
  isNote?: boolean;
  close: () => void;
  selectedRows: Row<TData>[];
  numOfAppUsers?: number;
}) {
  const navigate = useNavigate();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const platform = Object.values(PlatformName).find((value) => value === selectedApp?.platform.name);
  const [message, setMessage] = useState<string>("");

  const showCommonCommentsSettings = () => {
    if (selectedWorkspace)
      navigate(
        generatePath(RoutePaths.ADMINISTRATION_COMMON_COMMENTS, {
          workspaceSlug: selectedWorkspace?.slug,
        }),
        { replace: true },
      );
  };

  return (
    <Flex $column>
      <Styled.HeaderContainer>
        <ModalHeader heading={title} />
      </Styled.HeaderContainer>
      <Styled.Container>
        <Styled.MessageContainer>
          {!isNote && (
            <Styled.Helper>
              This message will be sent for selected tickets that have Users{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href={docLink(platform).registerAppUser}
              >
                registered
              </a>
              .
            </Styled.Helper>
          )}
          <MessageInput
            withoutSendButton={true}
            placeholder={
              !isNote
                ? "Reply to the app user"
                : `Leave an internal note — such as "Is this ready for the 2.1.5 release?" or "Check with the backend team first" — visible only to you and your teammates`
            }
            onChange={setMessage}
            onCommonCommentClick={(comment) => {
              setMessage(message ? message + "\n" + comment : comment);
            }}
            onCommonCommentSettingsClick={showCommonCommentsSettings}
            value={message}
            sendButtonText={!isNote ? "Send" : "Add note"}
            sendingButtonText={!isNote ? "Sending..." : "Adding..."}
          />
        </Styled.MessageContainer>
        <Styled.ButtonContainer>
          <Button
            type="submit"
            size="small"
            onClick={() => {
              confirmAction(
                selectedRows.map((row) => row.original),
                message,
                isNote,
              );
              close();
            }}
            tooltip={{
              text: numOfAppUsers === 0 ? "No users to send a message to" : "Please enter message first",
              position: "top",
              disabled: Boolean(message) && Boolean(!numOfAppUsers || (numOfAppUsers && numOfAppUsers > 0)),
            }}
            disabled={!message || numOfAppUsers === 0}
          >
            {isNote ? "Add note" : "Send message"}
          </Button>
        </Styled.ButtonContainer>
      </Styled.Container>
    </Flex>
  );
}
